import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building, Hash, Trash2, Edit, AlertCircle, BookOpen, Calendar, CreditCard, BarChart4 } from 'lucide-react';
import ChartOfAccountsService from '@/api/chartofaccounts.service';
import TransactionsService from '@/api/transactions.service';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import FormModal from '@/components/FormModal';
import { Loader2 } from 'lucide-react';

// Define necessary types inline to avoid import issues
type AccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
type TransactionType = 'DEBIT' | 'CREDIT';

interface ChartOfAccount {
  id?: number;
  publicId: string;
  code: string;
  name: string;
  type: AccountType;
  subtype?: string;
  description?: string;
  isActive: boolean;
  defaultAccount: boolean;
  parentId?: number;
  currency?: string;
  businessId?: number;
  timezone: string;
  dateUpdated?: string;
  dateCreated: string;
}

interface AccountTransaction {
  id?: number;
  publicId: string;
  businessId: number;
  chartOfAccountId: string;
  transactionType: TransactionType;
  amount: number;
  currency: string;
  description?: string;
  reference?: string;
  referenceId?: string;
  referenceType?: string;
  transactionDate: string;
  chartOfAccount?: ChartOfAccount;
  userName?: string;
}

import { columns as transactionColumns } from '../transactions/columns';
import { DataTable } from '@/components/data-table';

interface ChartOfAccountDetailProps {
  id?: string;
}

export default function ChartOfAccountDetail({ id: propsId }: ChartOfAccountDetailProps) {
  const params = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Use id from props or from params
  const id = propsId || params.id;

  const [account, setAccount] = useState<ChartOfAccount | null>(null);
  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    const fetchAccountData = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const response = await ChartOfAccountsService.getChartOfAccountById(id);
        
        if (response.data.result === 'success') {
          // Handle the response based on backend structure
          const accountData = response.data.chartOfAccount || response.data.section;
          const transactionsData = response.data.transactions || [];
          
          setAccount(accountData);
          setTransactions(transactionsData);
          setIsTransactionsLoading(false);
        } else {
          toast({
            title: 'Error',
            description: response.data.message,
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Error fetching account:', error);
        toast({
          title: 'Error',
          description: 'Failed to load account details',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchAccountData();
    }
  }, [id, toast]);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await ChartOfAccountsService.deleteChartOfAccount(id as string);
      if (response.data.result === 'success') {
        toast({
          title: 'Success',
          description: 'Account deleted successfully',
        });
        navigate('/chart-of-accounts');
      } else {
        toast({
          title: 'Error',
          description: response.data.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete account',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const accountTypeBadgeColor = (type: AccountType) => {
    switch (type) {
      case 'ASSET': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'LIABILITY': return 'bg-red-50 text-red-700 border-red-200';
      case 'EQUITY': return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'INCOME': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'EXPENSE': return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  // Calculate transaction totals
  const calculateTotals = () => {
    let debitTotal = 0;
    let creditTotal = 0;
    
    transactions.forEach(tx => {
      if (tx.transactionType === 'DEBIT') {
        debitTotal += tx.amount;
      } else {
        creditTotal += tx.amount;
      }
    });
    
    return {
      debitTotal: debitTotal / 100, // Assuming amount is stored in cents
      creditTotal: creditTotal / 100,
      balance: (creditTotal - debitTotal) / 100,
    };
  };

  const totals = calculateTotals();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center">
          <Card className="w-full max-w-lg">
            <CardContent className="flex flex-col items-center py-8">
              <AlertCircle className="h-12 w-12 text-slate-300 mb-4" />
              <h2 className="text-lg font-medium mb-2">Account Not Found</h2>
              <p className="text-slate-500 mb-6 text-center">
                The chart of account you're looking for doesn't exist or you don't have permission to view it.
              </p>
              <Button
                onClick={() => navigate('/chart-of-accounts')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Chart of Accounts
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={16} />
            Back
          </Button>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <span>{account.name}</span>
            <Badge variant="outline" className="font-mono">
              {account.code}
            </Badge>
          </h1>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="flex items-center gap-1"
            onClick={() => navigate(`/chart-of-accounts/edit/${id}`)}
          >
            <Edit size={16} />
            Edit
          </Button>
          <Button
            variant="destructive"
            className="flex items-center gap-1"
            onClick={() => setIsDeleteModalOpen(true)}
            disabled={account.defaultAccount || isDeleting}
            title={account.defaultAccount ? "Default accounts cannot be deleted" : ""}
          >
            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 size={16} />}
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Account Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Account Type</h3>
                <Badge variant="outline" className={`${accountTypeBadgeColor(account.type)}`}>
                  {account.type}
                </Badge>
                {account.subtype && (
                  <div className="mt-1 text-sm text-muted-foreground">
                    {account.subtype}
                  </div>
                )}
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Status</h3>
                <Badge variant={account.isActive ? "default" : "secondary"} className={account.isActive ? "bg-green-100 text-green-800" : ""}>
                  {account.isActive ? "Active" : "Inactive"}
                </Badge>
                {account.defaultAccount && (
                  <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200">
                    Default
                  </Badge>
                )}
              </div>
              
              {account.description && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Description</h3>
                  <p className="text-sm">{account.description}</p>
                </div>
              )}
              
              <Separator />
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Account ID</h3>
                <div className="flex items-center">
                  <Hash className="h-3.5 w-3.5 text-muted-foreground mr-1" />
                  <span className="text-sm font-mono">{account.publicId}</span>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Created</h3>
                <div className="flex items-center">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground mr-1" />
                  <span className="text-sm">{formatDate(account.dateCreated)}</span>
                </div>
              </div>
              
              {account.dateUpdated && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Last Updated</h3>
                  <div className="flex items-center">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground mr-1" />
                    <span className="text-sm">{formatDate(account.dateUpdated)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <div className="md:col-span-2">
          <Tabs defaultValue="transactions" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="transactions" className="flex items-center gap-1">
                <BookOpen className="h-4 w-4" /> Transactions
              </TabsTrigger>
              <TabsTrigger value="summary" className="flex items-center gap-1">
                <BarChart4 className="h-4 w-4" /> Summary
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="transactions">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg">Transactions</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/transactions/create', { state: { accountId: id } })}
                    >
                      Add Transaction
                    </Button>
                  </div>
                  <CardDescription>
                    {transactions.length ? 
                      `Showing ${transactions.length} transaction${transactions.length !== 1 ? 's' : ''}` : 
                      'No transactions found for this account'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <DataTable 
                    columns={transactionColumns} 
                    data={transactions} 
                    section="transactions" 
                    isLoading={isTransactionsLoading} 
                  />
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="summary">
              <div className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Account Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card className="bg-slate-50 border-0 shadow-none">
                        <CardContent className="p-4">
                          <div className="text-sm font-medium text-muted-foreground mb-1">Debits</div>
                          <div className="text-xl font-bold text-amber-600">
                            {account.currency || 'USD'} {totals.debitTotal.toFixed(2)}
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-slate-50 border-0 shadow-none">
                        <CardContent className="p-4">
                          <div className="text-sm font-medium text-muted-foreground mb-1">Credits</div>
                          <div className="text-xl font-bold text-blue-600">
                            {account.currency || 'USD'} {totals.creditTotal.toFixed(2)}
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-slate-50 border-0 shadow-none">
                        <CardContent className="p-4">
                          <div className="text-sm font-medium text-muted-foreground mb-1">Balance</div>
                          <div className={`text-xl font-bold ${totals.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {account.currency || 'USD'} {totals.balance.toFixed(2)}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Transaction History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px] flex items-center justify-center text-slate-400">
                      <div className="flex flex-col items-center">
                        <BarChart4 className="h-8 w-8 mb-2 text-slate-300" />
                        <p className="text-sm text-muted-foreground">Charts will be displayed here</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      <FormModal
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        title="Delete Account"
      >
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Are you sure you want to delete the account "{account.name}"? This action cannot be undone.
          </p>
          {transactions.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-700 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Warning</p>
                <p className="text-sm">
                  This account has {transactions.length} transaction{transactions.length !== 1 ? 's' : ''} associated with it. 
                  Deleting this account may impact reporting and accounting records.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Account
            </Button>
          </div>
        </div>
      </FormModal>
    </div>
  );
} 